import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import ComplianceForm from "@/components/compliance/ComplianceForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Download, Upload, RefreshCw, Filter, ChevronLeft, ChevronRight, ArrowUpDown, Trash2, X as XIcon, PencilLine } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatAED, formatDate, deadlineTone, daysBetween, periodLabel } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ImportDialog from "@/components/compliance/ImportDialog";
import logger from "@/lib/logger";

const TYPES = ["VAT", "Excise", "Accounting", "VAT Deregistration"];
const STATUSES = ["Not Started", "In Progress", "Waiting for Client Information", "Ready for Review", "Under Review", "Completed", "Overdue"];
const QUICK_STATUSES = STATUSES.filter((s) => s !== "Overdue");
const INVOICE_STATUSES = ["Invoice Pending", "Invoiced", "Partly Paid", "Paid"];
const PAGE_SIZE = 100;

function DeadlineBadge({ deadline, status }) {
  const tone = deadlineTone(deadline, status);
  const d = daysBetween(deadline);
  const cls = { red: "dl-red", orange: "dl-orange", yellow: "dl-yellow", green: "dl-green", slate: "dl-slate" }[tone];
  let text;
  if (status === "Completed") text = "Completed";
  else if (d === 0) text = "Today";
  else if (d > 0) text = `${d}d`;
  else text = `-${Math.abs(d)}d`;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium font-mono-num ${cls}`}>{text}</span>;
}

function StatusBadge({ status }) {
  const map = {
    "Not Started": "bg-slate-100 text-slate-700 border-slate-200",
    "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
    "Waiting for Client Information": "bg-amber-50 text-amber-700 border-amber-200",
    "Ready for Review": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Under Review": "bg-teal-50 text-teal-700 border-teal-200",
    "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Overdue": "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${map[status] || map["Not Started"]}`}>
      {status}
    </span>
  );
}

function ActionBadge({ value }) {
  const v = value || "Invoice Pending";
  const map = {
    "Invoice Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
    "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
    "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${map[v] || map["Invoice Pending"]}`}>{v}</span>;
}

function InlineTextCell({ record, field, placeholder, onChanged, testid }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(record[field] || "");
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setVal(record[field] || ""); }, [record, field]);
  const save = async () => {
    if (val === (record[field] || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/compliances/${record.id}`, { [field]: val });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      setVal(record[field] || "");
    } finally { setSaving(false); setEditing(false); }
  };
  if (!editing) {
    return (
      <button
        type="button"
        className="text-sm text-slate-800 hover:bg-slate-100 px-2 py-0.5 rounded-md text-left min-w-[80px] block"
        onClick={() => setEditing(true)}
        data-testid={testid}
        title="Click to edit"
      >
        {record[field] ? record[field] : <span className="text-slate-400 italic">{placeholder || "—"}</span>}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        else if (e.key === "Escape") { setVal(record[field] || ""); setEditing(false); }
      }}
      disabled={saving}
      placeholder={placeholder}
      className="h-7 text-xs w-[140px]"
      data-testid={`${testid}-input`}
    />
  );
}

function InvoiceStatusCell({ record, onChanged }) {
  const [saving, setSaving] = React.useState(false);
  const map = {
    "Invoice Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
    "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
    "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const current = record.action_2 || "Invoice Pending";
  const onSelect = async (v) => {
    if (v === current) return;
    setSaving(true);
    try {
      await api.patch(`/compliances/${record.id}`, { action_2: v });
      toast.success(`Invoice status → ${v}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Select value={current} onValueChange={onSelect} disabled={saving}>
      <SelectTrigger
        className={`h-7 px-2 text-xs w-[140px] border ${map[current] || map["Invoice Pending"]}`}
        data-testid={`invoice-status-${record.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {INVOICE_STATUSES.map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PriorityDot({ priority }) {
  const c = { Low: "bg-slate-300", Medium: "bg-blue-500", High: "bg-orange-500", Critical: "bg-red-600" }[priority] || "bg-slate-300";
  return <span className="inline-flex items-center gap-1.5 text-sm"><span className={`w-2 h-2 rounded-full ${c}`} />{priority}</span>;
}

const DEFAULT_MODULE_TYPES = ["VAT", "Accounting", "VAT Deregistration"];

export default function Compliances({
  moduleTitle = "VAT and Accounting",
  moduleTypes = DEFAULT_MODULE_TYPES,
  hideTypeColumn = false,
  hidePerson1 = true,  // hide Person 1 column + filter by default (belongs to CT)
  hidePriority = false,  // Excise: hide Priority column + filter
} = {}) {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const [q, setQ] = useState(sp.get("q") || "");
  const [type, setType] = useState(sp.get("compliance_type") || "");
  const [status, setStatus] = useState(sp.get("status") || "");
  const [periodFilter, setPeriodFilter] = useState(sp.get("period_label") || "");
  const [respName, setRespName] = useState(sp.get("responsible_name") || "");
  const [partnerId, setPartnerId] = useState(sp.get("partner_id") || "");
  const [overdueOnly, setOverdueOnly] = useState(sp.get("overdue_only") === "1");
  const [dueWithin, setDueWithin] = useState(sp.get("due_within") || "");
  const [mineOnly, setMineOnly] = useState(sp.get("mine_only") === "1");
  const [sortKey, setSortKey] = useState("deadline");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [person1, setPerson1] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [person1Options, setPerson1Options] = useState([]);
  const [reviewerOptions, setReviewerOptions] = useState([]);

  const canManage = ["administrator", "manager", "partner", "responsible_person"].includes(user?.role);
  const canDelete = user?.role === "administrator";

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) { logger.error("Failed to load users:", err); }
  }, []);

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (q) p.q = q;
    if (type) p.compliance_type = type;
    if (moduleTypes && moduleTypes.length) p.types = moduleTypes.join(",");
    if (status) p.status = status;
    if (periodFilter) p.period_label = periodFilter;
    if (respName) p.responsible_name = respName;
    if (person1) p.person_1 = person1;
    if (reviewer) p.reviewer = reviewer;
    if (partnerId) p.partner_id = partnerId;
    if (overdueOnly) p.overdue_only = true;
    if (dueWithin !== "") p.due_within = Number(dueWithin);
    if (mineOnly) p.mine_only = true;
    return { ...p, ...extra };
  }, [q, type, status, periodFilter, respName, person1, reviewer, partnerId, overdueOnly, dueWithin, mineOnly, moduleTypes]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams({ sort: sortKey, order: sortOrder, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE });
      const { data } = await api.get("/compliances", { params });
      setItems(data.items);
      setTotal(data.total);
      if (Array.isArray(data.period_labels)) setPeriodOptions(data.period_labels);
      if (Array.isArray(data.responsible_names)) setResponsibleOptions(data.responsible_names);
      if (Array.isArray(data.person_1_names)) setPerson1Options(data.person_1_names);
      if (Array.isArray(data.reviewer_names)) setReviewerOptions(data.reviewer_names);
    } catch (e) {
      toast.error("Failed to load compliances");
    } finally {
      setLoading(false);
    }
  }, [buildParams, sortKey, sortOrder, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Sync url search params (for shareable filters)
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (type) next.set("compliance_type", type);
    if (status) next.set("status", status);
    if (periodFilter) next.set("period_label", periodFilter);
    if (respName) next.set("responsible_name", respName);
    if (partnerId) next.set("partner_id", partnerId);
    if (overdueOnly) next.set("overdue_only", "1");
    if (dueWithin !== "") next.set("due_within", String(dueWithin));
    if (mineOnly) next.set("mine_only", "1");
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, type, status, periodFilter, respName, partnerId, overdueOnly, dueWithin, mineOnly]);

  const openId = sp.get("open");
  useEffect(() => {
    if (!openId) return;
    (async () => {
      try {
        const { data } = await api.get(`/compliances/${openId}`);
        setEditing(data);
        setShowForm(true);
      } catch (err) { logger.error("Failed to load compliance:", err); }
      const n = new URLSearchParams(sp);
      n.delete("open");
      setSp(n, { replace: true });
    })();
    // eslint-disable-next-line
  }, [openId]);

  const resetFilters = () => {
    setQ(""); setType(""); setStatus(""); setPeriodFilter(""); setRespName(""); setPartnerId("");
    setPerson1(""); setReviewer("");
    setOverdueOnly(false); setDueWithin(""); setMineOnly(false); setPage(1);
  };

  const sortToggle = (key) => {
    if (sortKey === key) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortOrder("asc"); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams(buildParams()).toString();
    const url = `${API_BASE}/compliances/export.csv${params ? "?" + params : ""}`;
    // include cookies
    window.open(url, "_blank");
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete compliance for ${c.client_name}?`)) return;
    try {
      await api.delete(`/compliances/${c.id}`);
      toast.success("Deleted");
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const recur = async (c) => {
    try {
      const { data } = await api.post(`/compliances/${c.id}/recur`);
      toast.success(`Next period created: ${periodLabel(data.return_period)}`);
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const setStatusQuick = async (c, s) => {
    try {
      await api.patch(`/compliances/${c.id}`, { status: s });
      toast.success("Status updated");
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allSelectedOnPage = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) items.forEach((c) => next.delete(c.id));
      else items.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected compliance record(s)?`)) return;
    try {
      const { data } = await api.post("/compliances/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} record(s)`);
      clearSelection();
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  return (
    <AppLayout
      title={moduleTitle}
      actions={
        <>
          <Button variant="outline" onClick={fetchItems} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          {canManage && (
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
          )}
          {canManage && (
            <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-compliance-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Compliance
            </Button>
          )}
        </>
      }
    >
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search client name…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" data-testid="filter-search" />
          </div>
          <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[150px]" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {(moduleTypes || TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={periodFilter || "all"} onValueChange={(v) => { setPeriodFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-period"><SelectValue placeholder="Period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All periods</SelectItem>
              {periodOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={respName || "all"} onValueChange={(v) => { setRespName(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[190px]" data-testid="filter-rp"><SelectValue placeholder="Responsible" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All responsible</SelectItem>
              {responsibleOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={person1 || "all"} onValueChange={(v) => { setPerson1(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className={"w-[170px]" + (hidePerson1 ? " hidden" : "")} data-testid="filter-person1"><SelectValue placeholder="Person 1" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Person 1</SelectItem>
              {person1Options.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={reviewer || "all"} onValueChange={(v) => { setReviewer(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[170px]" data-testid="filter-reviewer"><SelectValue placeholder="Reviewer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviewers</SelectItem>
              {reviewerOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setOverdueOnly((v) => !v); setPage(1); }}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                overdueOnly ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-300 text-slate-700")}
              data-testid="filter-overdue"
            >
              Overdue only
            </button>
            <button
              onClick={() => { setMineOnly((v) => !v); setPage(1); }}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                mineOnly ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 text-slate-700")}
              data-testid="filter-mine"
            >
              My records
            </button>
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-reset">Reset</Button>
          </div>
        </div>
        {dueWithin !== "" && (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
            <Filter className="w-3.5 h-3.5" />
            Showing due within <span className="font-mono-num font-medium">{dueWithin}</span> day(s).
            <button className="underline text-blue-700" onClick={() => setDueWithin("")}>clear</button>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="bulk-actions-bar">
          <div className="text-sm text-blue-900">
            <span className="font-semibold font-mono-num">{selected.size}</span> record(s) selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBulkEdit(true)} data-testid="bulk-edit-btn">
              <PencilLine className="w-4 h-4 mr-1.5" /> Bulk edit
            </Button>
            {canDelete && (
              <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="bulk-clear-btn">
              <XIcon className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="px-3 w-10">
                <Checkbox
                  checked={allSelectedOnPage}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all on page"
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              {!hideTypeColumn && <TableHead className="px-4"><SortButton label="Type" k="compliance_type" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} /></TableHead>}
              <TableHead><SortButton label="Client" k="client_name" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} /></TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Responsible</TableHead>
              {!hidePerson1 && <TableHead>Person 1</TableHead>}
              <TableHead>Reviewer</TableHead>
              <TableHead className="text-right">Fees</TableHead>
              <TableHead>Return Period</TableHead>
              <TableHead><SortButton label="Deadline" k="deadline" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} /></TableHead>
              <TableHead>Days</TableHead>
              {!hidePriority && <TableHead>Priority</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead>Invoice Status</TableHead>
              <TableHead className="text-right pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={(hideTypeColumn ? 14 : 15) - (hidePerson1 ? 1 : 0) - (hidePriority ? 1 : 0)} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={(hideTypeColumn ? 14 : 15) - (hidePerson1 ? 1 : 0) - (hidePriority ? 1 : 0)} className="text-center py-16 text-slate-500">No compliances match the current filters.</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} className="data-row" data-testid={`row-${c.id}`}>
                <TableCell className="px-3">
                  <Checkbox
                    checked={selected.has(c.id)}
                    onCheckedChange={() => toggleSelect(c.id)}
                    aria-label={`Select ${c.client_name}`}
                    data-testid={`row-check-${c.id}`}
                  />
                </TableCell>
                {!hideTypeColumn && (
                  <TableCell className="px-4">
                    <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-600">{c.compliance_type}</span>
                  </TableCell>
                )}
                <TableCell className="font-medium text-slate-900">{c.client_name}</TableCell>
                <TableCell><InlineTextCell record={c} field="partner_name" placeholder="Add partner" onChanged={fetchItems} testid={`partner-${c.id}`} /></TableCell>
                <TableCell><InlineTextCell record={c} field="responsible_person_name" placeholder="Add responsible" onChanged={fetchItems} testid={`responsible-${c.id}`} /></TableCell>
                {!hidePerson1 && <TableCell><InlineTextCell record={c} field="person_1" placeholder="Add person 1" onChanged={fetchItems} testid={`person1-${c.id}`} /></TableCell>}
                <TableCell><InlineTextCell record={c} field="reviewer" placeholder="Add reviewer" onChanged={fetchItems} testid={`reviewer-${c.id}`} /></TableCell>
                <TableCell className="text-right font-mono-num text-slate-800">{formatAED(c.fees)}</TableCell>
                <TableCell className="text-sm text-slate-700 font-mono-num">{periodLabel(c.return_period)}</TableCell>
                <TableCell className="font-mono-num text-slate-800">{formatDate(c.deadline)}</TableCell>
                <TableCell><DeadlineBadge deadline={c.deadline} status={c.status} /></TableCell>
                {!hidePriority && <TableCell><PriorityDot priority={c.priority} /></TableCell>}
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell><InvoiceStatusCell record={c} onChanged={fetchItems} /></TableCell>
                <TableCell className="text-right pr-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`actions-${c.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`edit-${c.id}`}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Quick status</DropdownMenuLabel>
                      {QUICK_STATUSES.map((s) => (
                        <DropdownMenuItem key={s} onClick={() => setStatusQuick(c, s)}>{s}</DropdownMenuItem>
                      ))}
                      {c.compliance_type === "VAT" && (c.return_period?.kind === "monthly" || c.return_period?.kind === "quarterly") && canManage && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => recur(c)} data-testid={`recur-${c.id}`}>Create next period</DropdownMenuItem>
                        </>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={() => remove(c)} data-testid={`delete-${c.id}`}>
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-600">
          <div>Showing <span className="font-medium">{items.length}</span> of <span className="font-medium font-mono-num">{total}</span> records</div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs px-2 font-mono-num">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <ComplianceForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        initial={editing}
        users={users}
        onSaved={fetchItems}
        canEditAssignment={canManage}
      />
      <ImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onDone={fetchItems}
      />
      <BulkEditComplianceDialog
        open={showBulkEdit}
        onClose={() => setShowBulkEdit(false)}
        ids={Array.from(selected)}
        onDone={() => { clearSelection(); fetchItems(); }}
      />
    </AppLayout>
  );
}

function BulkEditComplianceDialog({ open, onClose, ids, onDone }) {
  const [fields, setFields] = React.useState({
    status: { enabled: false, value: "In Progress" },
    priority: { enabled: false, value: "Medium" },
    action_2: { enabled: false, value: "Invoiced" },
    partner_name: { enabled: false, value: "" },
    responsible_person_name: { enabled: false, value: "" },
    person_1: { enabled: false, value: "" },
    reviewer: { enabled: false, value: "" },
  });
  const [saving, setSaving] = React.useState(false);
  const submit = async () => {
    const updates = {};
    Object.entries(fields).forEach(([k, v]) => { if (v.enabled) updates[k] = v.value; });
    if (Object.keys(updates).length === 0) {
      toast.error("Enable at least one field to update");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/compliances/bulk-edit", { ids, updates });
      toast.success(`Updated ${data.updated} record(s)`);
      setFields((f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, { ...v, enabled: false }])));
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk edit failed");
    } finally { setSaving(false); }
  };
  const rowFor = (key, label, control) => (
    <div className="grid grid-cols-[24px_170px_1fr] gap-2 items-center">
      <Checkbox
        checked={fields[key].enabled}
        onCheckedChange={(v) => setFields((f) => ({ ...f, [key]: { ...f[key], enabled: !!v } }))}
        data-testid={`c-bulk-edit-toggle-${key}`}
      />
      <Label className="text-xs uppercase tracking-widest text-slate-600">{label}</Label>
      <div className={fields[key].enabled ? "" : "opacity-50 pointer-events-none"}>{control}</div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk edit {ids.length} record(s)</DialogTitle>
          <DialogDescription>Only the enabled fields will be updated. Others are left unchanged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="c-bulk-edit-body">
          {rowFor("status", "Status",
            <Select value={fields.status.value} onValueChange={(v) => setFields((f) => ({ ...f, status: { ...f.status, value: v } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {rowFor("priority", "Priority",
            <Select value={fields.priority.value} onValueChange={(v) => setFields((f) => ({ ...f, priority: { ...f.priority, value: v } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Low", "Medium", "High", "Critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {rowFor("action_2", "Invoice Status",
            <Select value={fields.action_2.value} onValueChange={(v) => setFields((f) => ({ ...f, action_2: { ...f.action_2, value: v } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {rowFor("partner_name", "Partner",
            <Input value={fields.partner_name.value} onChange={(e) => setFields((f) => ({ ...f, partner_name: { ...f.partner_name, value: e.target.value } }))} />
          )}
          {rowFor("responsible_person_name", "Responsible",
            <Input value={fields.responsible_person_name.value} onChange={(e) => setFields((f) => ({ ...f, responsible_person_name: { ...f.responsible_person_name, value: e.target.value } }))} />
          )}
          {rowFor("person_1", "Person 1",
            <Input value={fields.person_1.value} onChange={(e) => setFields((f) => ({ ...f, person_1: { ...f.person_1, value: e.target.value } }))} />
          )}
          {rowFor("reviewer", "Reviewer",
            <Input value={fields.reviewer.value} onChange={(e) => setFields((f) => ({ ...f, reviewer: { ...f.reviewer, value: e.target.value } }))} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="c-bulk-edit-submit">
            {saving ? "Applying…" : "Apply bulk edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SortButton({ label, k, sortKey, sortOrder, onClick }) {
  const active = sortKey === k;
  return (
    <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => onClick(k)}>
      {label}
      <ArrowUpDown className={cn("w-3 h-3", active ? "text-blue-600" : "text-slate-400")} />
      {active && <span className="text-[10px] text-slate-500 font-mono-num">{sortOrder}</span>}
    </button>
  );
}
